import { useRef, useState, useEffect } from "react";
import { Camera, Loader2, Upload, AlertTriangle, CheckCircle2, X, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { extractIngredientsFromPhoto, fetchPhotoOcrUsage } from "../lib/api";

const MAX_DIM = 1920;            // resize longest side — matches product spec
const QUALITY = 0.85;            // JPEG quality after resize
const COMPRESS_THRESHOLD = 2 * 1024 * 1024;  // only compress if > 2MB
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;   // server cap

// Rough base64 payload size — string length ~ bytes * 4/3 plus the data-url prefix.
function approxBytesFromDataUrl(dataUrl) {
  const comma = dataUrl.indexOf(",");
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  return Math.ceil((b64.length * 3) / 4);
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function compressDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const scale = Math.min(MAX_DIM / width, MAX_DIM / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", QUALITY));
    };
    img.src = dataUrl;
  });
}

export default function PhotoCapture({
  open,
  onOpenChange,
  onConfirm,
  onLimitReached,
  pasteOnly = false,
}) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unreadable, setUnreadable] = useState(false);
  const [draft, setDraft] = useState("");
  const [usage, setUsage] = useState(null); // {used, limit, remaining}

  // Refresh the usage tile every time the dialog opens — keeps the
  // "X / 3 free photo scans used" copy honest after every scan.
  useEffect(() => {
    if (!open || pasteOnly) return;
    let cancelled = false;
    fetchPhotoOcrUsage()
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, pasteOnly]);

  const reset = () => {
    setPreview(null);
    setBusy(false);
    setUnreadable(false);
    setDraft("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("That file type isn't supported", {
        description: "Please choose a JPEG, PNG, or WEBP image — those are easiest for our OCR to read.",
      });
      return;
    }
    setBusy(true);
    setUnreadable(false);
    setDraft("");
    let compressToastId;
    try {
      let dataUrl = await fileToDataUrl(file);
      // Only compress large files — small ones ship as-is to preserve fidelity.
      if (file.size > COMPRESS_THRESHOLD) {
        compressToastId = toast.loading("Optimising image…");
        dataUrl = await compressDataUrl(dataUrl);
        toast.dismiss(compressToastId);
        compressToastId = undefined;
      }
      if (approxBytesFromDataUrl(dataUrl) > MAX_UPLOAD_BYTES) {
        toast.error("That image is still too large", {
          description: "Try a tighter crop of just the ingredient list, or a slightly smaller resolution.",
        });
        setBusy(false);
        return;
      }
      setPreview(dataUrl);
      const res = await extractIngredientsFromPhoto(dataUrl);
      if (res?.usage) setUsage(res.usage);
      if (res?.unreadable || !res?.ingredients?.length) {
        setUnreadable(true);
        toast.message("We couldn't read that photo", {
          description: "Try a sharper, better-lit close-up of the ingredient list — or type the ingredients below.",
        });
      } else {
        setDraft(res.ingredients.join(", "));
        toast.success(`Read ${res.ingredients.length} ingredient${res.ingredients.length === 1 ? "" : "s"}`, {
          description: "Review the list below and tap Confirm when it looks right.",
        });
      }
    } catch (e) {
      if (compressToastId) toast.dismiss(compressToastId);
      const detail = e?.response?.data?.detail;
      // Free-tier session limit reached — let the parent show the limit
      // modal with the "Create account" / "Paste ingredients" CTAs.
      if (e?.response?.status === 402 && detail?.kind === "photo_ocr") {
        setUsage({
          used: detail.used,
          limit: detail.limit,
          remaining: detail.remaining ?? 0,
        });
        onOpenChange(false);
        reset();
        onLimitReached?.({
          used: detail.used,
          limit: detail.limit,
        });
        return;
      }
      const msg = detail || e.message || "We couldn't extract ingredients from that photo.";
      toast.error("Photo couldn't be processed", {
        description: typeof msg === "string" ? msg : "Try a different photo, or type the ingredients manually.",
      });
    } finally {
      setBusy(false);
    }
  };

  const onConfirmList = () => {
    const list = draft
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!list.length) {
      toast.warning("Add at least one ingredient", {
        description: "Type or paste at least one ingredient before confirming the list.",
      });
      return;
    }
    onConfirm?.({ ingredients: list, raw: draft });
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent
        data-testid="photo-capture-dialog"
        className="bg-cream-50 border-surface-line rounded-3xl max-w-md p-7 sm:p-8"
      >
        <DialogTitle className="text-[26px] font-extrabold tracking-tight text-ink leading-tight">
          {pasteOnly ? "Paste ingredients" : "Scan ingredients"}
        </DialogTitle>
        <DialogDescription className="text-ink-soft/85 mt-2 text-[14px]">
          {pasteOnly
            ? "Paste the comma-separated ingredient list from the back of your product. We'll run the safety check next."
            : "Snap a photo of the back-of-pack ingredient list. We'll extract the names and let you edit before we run the safety check."}
        </DialogDescription>

        {/* Per-session free-tier badge — only when running OCR, not paste-only */}
        {!pasteOnly && usage && (
          <div className="mt-3 inline-flex items-center gap-2 self-start">
            <span
              data-testid="photo-capture-usage"
              className="inline-flex items-center px-2 h-6 rounded-full bg-cream-100 border border-surface-line text-[11px] uppercase tracking-[0.16em] font-bold text-ink-mute"
            >
              {usage.used} / {usage.limit} free scans used
            </span>
          </div>
        )}

        {/* File input — `capture` opens the rear camera on phones; falls back to gallery on desktop */}
        <input
          ref={fileInputRef}
          data-testid="photo-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="sr-only"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        {/* Paste-only mode: skip the camera tile and render the textarea directly */}
        {pasteOnly && (
          <div className="mt-5 space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardPaste className="w-4 h-4 text-ink-mute" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-ink-mute">
                Ingredients (comma separated)
              </span>
            </div>
            <textarea
              data-testid="photo-paste-only-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={6}
              placeholder="Aqua, Glycerin, Niacinamide, Sodium Hyaluronate, Phenoxyethanol…"
              className="w-full rounded-xl border border-surface-line bg-white px-3 py-2 text-[14px] focus:outline-none focus:ring-4 focus:ring-peach-100 focus:border-peach-200"
            />
            <Button
              type="button"
              data-testid="photo-paste-only-confirm"
              onClick={onConfirmList}
              disabled={!draft.trim()}
              className="w-full h-12 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-semibold"
            >
              Use these ingredients
            </Button>
          </div>
        )}

        {!pasteOnly && !preview && (
          <div className="mt-5 rounded-2xl border-2 border-dashed border-surface-line bg-white p-6 text-center">
            <Camera className="h-8 w-8 text-ink-mute mx-auto" />
            <p className="text-sm text-ink-soft mt-2 max-w-xs mx-auto">
              Make sure the ingredient text is in focus and fills the frame.
            </p>
            <Button
              type="button"
              data-testid="photo-capture-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="mt-4 h-11 rounded-xl bg-ink hover:bg-ink-soft text-cream-50"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Take or choose a photo
            </Button>
          </div>
        )}

        {!pasteOnly && preview && (
          <div className="mt-5 space-y-4">
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-black border border-surface-line">
              <img src={preview} alt="ingredient label" className="absolute inset-0 w-full h-full object-contain" />
              {busy && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-cream-50">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Reading the label…
                </div>
              )}
              <button
                type="button"
                onClick={reset}
                aria-label="Retake photo"
                data-testid="photo-retake-btn"
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-black/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {unreadable && !busy && (
              <div className="rounded-xl border border-caution-border bg-caution-bg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-caution-deep mt-0.5 shrink-0" />
                <div className="text-[13px] text-caution-deep leading-snug">
                  Could not read ingredient list. Please take a clearer photo or enter ingredients manually.
                </div>
              </div>
            )}

            {!busy && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">
                    Ingredients (edit if needed)
                  </span>
                  {!unreadable && draft && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-safe-deep">
                      <CheckCircle2 className="w-3 h-3" /> Extracted
                    </span>
                  )}
                </div>
                <textarea
                  data-testid="photo-ingredients-textarea"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={5}
                  placeholder="Niacinamide, Hyaluronic Acid, Glycerin, …"
                  className="w-full rounded-xl border border-surface-line bg-white px-3 py-2 text-[14px] focus:outline-none focus:ring-4 focus:ring-peach-100 focus:border-peach-200"
                />
                <Button
                  type="button"
                  data-testid="photo-confirm-btn"
                  onClick={onConfirmList}
                  disabled={!draft.trim()}
                  className="w-full h-12 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-semibold"
                >
                  Use these ingredients
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 text-[11px] text-ink-mute leading-relaxed">
          We send the photo to our AI vision model to read the label. The image isn't stored.
        </div>
      </DialogContent>
    </Dialog>
  );
}
